// Background TikZ → SVG prerender queue.
//
// Runs after a worksheet is shown to compile every uncached TikZ diagram
// in an offscreen iframe so subsequent "View Diagram" clicks render
// instantly from cache. Renders are strictly serial (TikZJax is heavy on
// WASM init; parallel iframes thrash memory) and yield to the browser
// between jobs via requestIdleCallback so the UI stays responsive.
//
// Cancellation: pass an AbortSignal. Aborting it stops the loop and tears
// down the in-flight iframe immediately.

import { cacheSvg, getCachedSvg, postSvgCacheToServer, postSvgCacheToQbQuestion } from './tikzSvgCache';
import { renderTikzToSvg } from './tikzRender';

const DEFAULT_TIMEOUT_MS = 25000;
const IDLE_TIMEOUT_MS = 200;

const sanitizeTikz = (value = '') =>
  String(value).replace(/[^\x09\x0A\x0D\x20-\x7E]/g, '');

const yieldToBrowser = () =>
  new Promise((resolve) => {
    if (typeof window === 'undefined') {
      resolve();
      return;
    }
    if (typeof window.requestIdleCallback === 'function') {
      window.requestIdleCallback(() => resolve(), { timeout: IDLE_TIMEOUT_MS });
      return;
    }
    window.setTimeout(resolve, 50);
  });

// Dedupe TikZ codes by sanitized text and drop ones already cached.
const buildUncachedJobs = (jobs = []) => {
  const seen = new Set();
  const out = [];
  for (const job of jobs) {
    if (!job) continue;
    const rawCode = typeof job === 'string' ? job : job.tikzCode;
    const sanitized = sanitizeTikz(rawCode || '');
    if (!sanitized) continue;
    if (seen.has(sanitized)) continue;
    seen.add(sanitized);
    if (getCachedSvg(sanitized)) continue;
    out.push({
      tikzCode: sanitized,
      worksheetId: typeof job === 'object' ? job.worksheetId : undefined,
      qbQuestionId: typeof job === 'object' ? job.qbQuestionId : undefined,
    });
  }
  return out;
};

// Group write-backs by worksheet id so each worksheet POSTs a single batch
// instead of one request per diagram. qbQuestionId entries are sent
// individually because the server endpoint is per-question.
const flushServerCaches = (writebackBuffers, worksheetIdByQueue) => {
  for (const [worksheetId, entries] of writebackBuffers.entries()) {
    if (!entries.length) continue;
    postSvgCacheToServer(worksheetId, entries);
  }
  writebackBuffers.clear();
  // worksheetIdByQueue is unused but kept for future tracking; receivers of
  // postSvgCacheToServer ignore unknown hashes server-side.
  void worksheetIdByQueue;
};

// Run the queue. Returns { rendered, failed, aborted }. Each successful
// render is also pushed back to the server cache so other devices reuse
// it. Failures are swallowed silently — the diagram simply stays as a
// "View Diagram" placeholder until the user opens the modal.
export const prerenderTikzList = async (jobs = [], { signal = null, timeoutMs = DEFAULT_TIMEOUT_MS } = {}) => {
  const queue = buildUncachedJobs(jobs);
  if (queue.length === 0) {
    return { rendered: 0, failed: 0, aborted: false };
  }

  let rendered = 0;
  let failed = 0;
  const writebacksByWorksheet = new Map();

  for (const { tikzCode, worksheetId, qbQuestionId } of queue) {
    if (signal && signal.aborted) {
      flushServerCaches(writebacksByWorksheet);
      return { rendered, failed, aborted: true };
    }
    // Cache may have been populated by a foreground render that ran while
    // we were yielding — re-check before spinning up another iframe.
    if (getCachedSvg(tikzCode)) continue;

    try {
      const svgHtml = await renderTikzToSvg(tikzCode, { timeoutMs, signal });
      if (signal && signal.aborted) {
        flushServerCaches(writebacksByWorksheet);
        return { rendered, failed, aborted: true };
      }
      if (svgHtml) {
        cacheSvg(tikzCode, svgHtml);
        rendered += 1;
        if (worksheetId) {
          const existing = writebacksByWorksheet.get(worksheetId);
          const entry = { tikzCode, svgHtml };
          if (existing) existing.push(entry);
          else writebacksByWorksheet.set(worksheetId, [entry]);
        }
        if (qbQuestionId) {
          postSvgCacheToQbQuestion(qbQuestionId, { tikzCode, svgHtml });
        }
      } else {
        failed += 1;
      }
    } catch (_renderError) {
      failed += 1;
    }
    await yieldToBrowser();
  }

  flushServerCaches(writebacksByWorksheet);
  return { rendered, failed, aborted: false };
};
